import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Tag } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import FormInput from '../FormInput';
import Pagination from '../Pagination';
import api from '../api';

const CategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedCategorie, setSelectedCategorie] = useState(null);

  const [formData, setFormData] = useState({
    libelle: '',
    description: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/finance/categories-depense/');
      setCategories(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des catégories:', error);
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add') {
        await api.post('/finance/categories-depense/', formData);
        toast.success('Catégorie ajoutée avec succès');
      } else {
        await api.put(`/finance/categories-depense/${selectedCategorie.id}/`, formData);
        toast.success('Catégorie modifiée avec succès');
      }

      fetchCategories();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde de la catégorie');
    }
  };

  const handleEdit = (categorie) => {
    setSelectedCategorie(categorie);
    setFormData({
      libelle: categorie.libelle,
      description: categorie.description || ''
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
      try {
        await api.delete(`/finance/categories-depense/${id}/`);
        fetchCategories();
        toast.success('Catégorie supprimée avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de la catégorie');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      libelle: '',
      description: ''
    });
    setSelectedCategorie(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode('add');
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Filtrage des données
  const filteredCategories = categories.filter(categorie => {
    return categorie.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           categorie.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton d'ajout et recherche */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative">
          <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une catégorie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
          />
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouvelle Catégorie
        </button>
      </div>

      {/* Liste des catégories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Tag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Aucune catégorie trouvée</p>
          </div>
        ) : (
          currentItems.map((categorie) => (
            <div key={categorie.id} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <Tag className="h-8 w-8 text-[#202654] mr-3" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{categorie.libelle}</h3>
                    <p className="text-sm text-gray-500">Créé le {formatDate(categorie.create_at)}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(categorie)}
                    className="text-blue-600 hover:text-blue-900 transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(categorie.id)}
                    className="text-red-600 hover:text-red-900 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {categorie.description && (
                <p className="text-gray-600 text-sm mb-4">{categorie.description}</p>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <span className="text-sm text-gray-500">
                  {categorie.depenses_count || 0} dépense(s)
                </span>
                <span className="text-xs text-gray-400">
                  Modifié le {formatDate(categorie.update_at)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Modal d'ajout/édition */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalMode === 'add' ? 'Nouvelle Catégorie' : 'Modifier la Catégorie'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput
            label="Libellé"
            type="text"
            value={formData.libelle}
            onChange={(e) => handleInputChange('libelle', e.target.value)}
            placeholder="Ex: Salaires, Fournitures, etc."
            required
          />

          <FormInput
            label="Description"
            type="textarea"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Description de la catégorie (optionnel)"
            rows={3}
          />

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f45] transition-colors"
            >
              {modalMode === 'add' ? 'Ajouter' : 'Modifier'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CategoriesTab; 